// kanjo/Delivery.jsx — Delivery tab: monthly platform statements + reports.
//
// Routes:
//   /delivery         → DeliveryMenu   (Pro-gated home: quota, report, list)
//   /delivery/upload  → DeliveryUpload (pick PDF + platform/month → OCR → save)
//   /delivery/detail  → DeliveryDetail (review/correct/delete one statement)
//
// Delivery is a Pro feature (entitlements.features.delivery). Non-entitled users
// see an upsell. Statements are MONTHLY platform PDFs, OCR'd server-side; the
// client normalizes to canonical buckets (window.kanjo.delivery).

// ── helpers ──────────────────────────────────────────────────
const D = () => (window.kanjo && window.kanjo.delivery) || null;

function money(cents) {
  const c = window.kanjo?.demo?.scale ? window.kanjo.demo.scale(cents) : cents;
  const v = (Number(c) || 0) / 100;
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}
// JSX-rendering money used in places we want the small-grey-cents treatment
// (Today hero style). Plain string `money()` is still used in titles/aria.
function $$(cents, opts = {}) {
  return <KMoney cents={cents} currency="USD" locale="en-US" {...opts}/>;
}
function pct(frac) {
  return frac == null ? '—' : `${(frac * 100).toFixed(1)}%`;
}
function thisMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function monthLabel(ym) {
  if (!ym) return 'Unknown month';
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, (m || 1) - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}
function monthShort(ym) {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, (m || 1) - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}
function daysInMonthOf(ym) {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m, 0).getDate(); // m is 1-based; day 0 of next month = last day
}
// Compact money for axis/bar labels: $46.9k / $1.2k / $940.
function kMoney(cents) {
  const c = window.kanjo?.demo?.scale ? window.kanjo.demo.scale(cents) : cents;
  const v = (Number(c) || 0) / 100;
  const a = Math.abs(v);
  if (a >= 1000) return `${v < 0 ? '-' : ''}$${(a / 1000).toFixed(1)}k`;
  return `${v < 0 ? '-' : ''}$${a.toFixed(0)}`;
}
function platformMeta(id) {
  const P = D()?.PLATFORMS || {};
  return P[id] || { id, label: id ? String(id) : 'Unknown', color: 'var(--ink-3)' };
}

const COST_TARGET = 0.30;
const BURGUNDY = '#8a1f3c'; // Total-tab bar/accent color
// Cost ratio = (sales − net payout) / sales. The number operators watch.
function costRatioOf(b) {
  const g = b.grossSalesCents || 0;
  return g > 0 ? (g - (b.netPayoutCents || 0)) / g : null;
}
// Green under target, red at/over (hard 30% line).
function costColor(ratio) {
  if (ratio == null) return 'var(--ink-3)';
  return ratio < COST_TARGET ? 'var(--positive)' : 'var(--danger)';
}
// Pill showing the cost % against the 30% target.
function CostPill({ b, big = false }) {
  const r = costRatioOf(b);
  if (r == null) return null;
  const c = costColor(r);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5 }}>
      <KPct value={r * 100} style={{ color: c, fontWeight: 700, fontSize: big ? 22 : 14 }}/>
      <span style={{ fontSize: big ? 11 : 10, fontWeight: 600, opacity: 0.8, color: c }}>cost</span>
    </span>
  );
}

// Hook: resolve whether the user is entitled to delivery (Pro / payg add-on).
function useDeliveryEntitlement() {
  const [state, setState] = React.useState({ loading: true, entitled: false });
  React.useEffect(() => {
    let live = true;
    (async () => {
      try {
        const plan = window.kanjo?.scale?.plan;
        const p = await plan?.getPlan?.();
        const ent = plan?.entitlements?.(p);
        if (live) setState({ loading: false, entitled: !!ent?.features?.delivery });
      } catch { if (live) setState({ loading: false, entitled: false }); }
    })();
    return () => { live = false; };
  }, []);
  return state;
}

// ── Pro upsell (shown when not entitled) ─────────────────────
function DeliveryUpsell() {
  const nav = React.useContext(KanjoNav);
  return (
    <KShell tab="delivery">
      <KHeader big back={false} title={t('header.delivery', 'Delivery')} subtitle={t('sub.delivery', 'DoorDash, Uber Eats & Grubhub — in one place.')} />
      <div style={{ padding: '8px 20px 24px' }}>
        <Empty
          icon={<IcoCar />}
          title="Delivery analysis is a Pro feature"
          body="Upload your monthly platform statements to see sales, commissions, fees and marketing — aggregated and per platform — plus an estimate of how much of your COGS delivery is eating."
        />
        <button className="btn lg" onClick={() => nav.navigate('/account/subscribe')}
                style={{ width: '100%', marginTop: 16, cursor: 'pointer' }}>
          Upgrade to Pro
        </button>
      </div>
    </KShell>
  );
}


// Why is delivery empty? Renders the diagnose() walk of the pipeline: how each
// channel classifies, which bank payouts were recognized, and what attribution
// wrote. Shown only when the month has no delivery money, so a genuine quiet
// month is still explained rather than left ambiguous.
function DeliveryWhyEmpty({ why }) {
  const [open, setOpen] = React.useState(false);
  const problems = why.problems || [];
  const payouts = why.recognizedPayouts || [];
  const matched = payouts.reduce((s, p) => s + (p.count || 0), 0);
  const headline = problems[0]
    || (matched === 0 ? 'No delivery payouts recognized in the bank feed yet.'
      : `No sale days booked for ${why.month} yet.`);
  return (
    <div className="card" style={{ padding: 14, marginBottom: 18, borderLeft: '3px solid var(--warning)' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>No delivery sales this month</div>
      <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 4, lineHeight: 1.5 }}>{headline}</div>
      <button onClick={() => setOpen((v) => !v)}
        style={{ marginTop: 8, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
          color: 'var(--brand)', fontSize: 12, fontWeight: 600 }}>
        {open ? 'Hide details' : 'Show details'}
      </button>
      {open && (
        <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--ink-2)', lineHeight: 1.6 }}>
          <div style={{ fontWeight: 600, color: 'var(--ink)' }}>Channels</div>
          {(why.channels || []).map((c) => (
            <div key={c.id} style={{ fontFamily: 'monospace' }}>{c.name} · type={c.type} · {c.kind}</div>
          ))}
          <div style={{ fontWeight: 600, color: 'var(--ink)', marginTop: 8 }}>Payouts recognized</div>
          {payouts.length === 0 && <div>none</div>}
          {payouts.map((p) => (
            <div key={p.channel} style={{ fontFamily: 'monospace' }}>
              {p.channel}: {p.count}{p.latest && p.latest.length ? ` — ${p.latest.join(' · ')}` : ''}
            </div>
          ))}
          <div style={{ fontWeight: 600, color: 'var(--ink)', marginTop: 8 }}>Sale days written ({why.month})</div>
          {(why.saleDaysInMonth || []).length === 0 && <div>none</div>}
          {(why.saleDaysInMonth || []).map((d) => <div key={d} style={{ fontFamily: 'monospace' }}>{d}</div>)}
          {problems.length > 1 && (
            <div style={{ marginTop: 8, color: 'var(--danger)' }}>{problems.slice(1).join(' · ')}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── /delivery — home ─────────────────────────────────────────
function DeliveryMenu({ dark = false }) {
  useT();
  const nav = React.useContext(KanjoNav);
  const ent = useDeliveryEntitlement();
  const [st, setSt] = React.useState({ loading: true });
  const [pickedMonth, setPickedMonth] = React.useState(null); // null = default to latest

  React.useEffect(() => {
    if (ent.loading || !ent.entitled) return;
    let live = true;
    (async () => {
      try {
        // Rebuild the sale-dated ledger before reading it. Only Today did this,
        // so opening Delivery directly showed whatever the last sync left behind.
        await window.kanjo.revenue.sales.syncFromDeposits().catch(() => {});
        const all = await D().listStatements();
        // Current-month restaurant share from DEPOSITS (statements for the live
        // month aren't uploaded yet): delivery-channel net payouts, per platform.
        // Restaurant share = the delivery-platform money that actually landed in
        // the bank (matched deposits → delivery-channel salesEntries). Computed for
        // three windows from a single query: month-to-date, year-to-date, and the
        // trailing 12 months (month-aligned).
        let mtd = null, mtdMonth = null, ytd = null, ttm = null, ytdYear = null;
        try {
          const claims = await window.kanjo.auth.currentClaims();
          const userId = claims.sub;
          const db = window.kanjo.db;
          const today = localToday();
          mtdMonth = today.slice(0, 7);
          const d0 = new Date(today + 'T00:00:00Z');
          ytdYear = d0.getUTCFullYear();
          const yearStart = `${ytdYear}-01-01`;
          const monthStart = `${mtdMonth}-01`;
          // First day of the month 11 months ago → a 12-month window ending this month.
          const ttmStart = new Date(Date.UTC(d0.getUTCFullYear(), d0.getUTCMonth() - 11, 1)).toISOString().slice(0, 10);
          const windowStart = ttmStart < yearStart ? ttmStart : yearStart;

          const channels = await db.channels.where('userId').equals(userId).toArray();
          // Resolve the platform via the shared resolver (type, then name) — a
          // channel carrying only a name used to test false here and the whole
          // delivery tile read $0 even though the sale days were written.
          const platOf = window.kanjo.revenue.depositRules.deliveryPlatformOf;
          const chPlat = new Map();
          for (const c of channels) { const p = platOf(c); if (p) chPlat.set(c.channelId, p); }
          const entries = await db.salesEntries.where('[userId+date]').between([userId, windowStart], [userId, today], true, true).toArray();
          const mk = () => ({ doordash: 0, ubereats: 0, grubhub: 0, total: 0 });
          const accMtd = mk(), accYtd = mk(), accTtm = mk();
          for (const e of entries) {
            const p = chPlat.get(e.channelId); if (!p) continue;
            const c = e.netSalesCents || 0;
            if (e.date >= ttmStart)   { accTtm[p] += c; accTtm.total += c; }
            if (e.date >= yearStart)  { accYtd[p] += c; accYtd.total += c; }
            if (e.date >= monthStart) { accMtd[p] += c; accMtd.total += c; }
          }
          if (accMtd.total > 0) mtd = accMtd;
          if (accYtd.total > 0) ytd = accYtd;
          if (accTtm.total > 0) ttm = accTtm;
        } catch (e) {
          // Same rule as Today: an exception here used to be indistinguishable
          // from "no delivery deposits yet" — both hid the tile.
          console.error('[Delivery] deposit rollup failed — tile hidden', e);
        }
        // Delivery reading $0 has been impossible to explain from the screen —
        // an empty month, a dropped deposit and a thrown error all render the
        // same. When there is nothing to show, ask the pipeline WHY and show it.
        let why = null;
        if (!mtd || !mtd.total) {
          try { why = await window.kanjo.revenue.sales.diagnose({ kind: 'delivery' }); }
          catch (e) { why = { problems: [`diagnose failed: ${e?.message || e}`] }; }
        }
        if (live) setSt({ loading: false, all, mtd, mtdMonth, ytd, ttm, ytdYear, why });
      } catch (e) { if (live) setSt({ loading: false, error: String(e?.message || e) }); }
    })();
    return () => { live = false; };
  }, [ent.loading, ent.entitled]);

  if (ent.loading) return <KShell tab="delivery"><KHeader big back={false} title={t('header.delivery', 'Delivery')} /><div style={{ padding: 20, color: 'var(--on-dim)' }}>Loading…</div></KShell>;
  if (!ent.entitled) return <DeliveryUpsell />;
  if (st.loading) return <KShell tab="delivery"><KHeader big back={false} title={t('header.delivery', 'Delivery')} /><div style={{ padding: 20, color: 'var(--on-dim)' }}>Loading…</div></KShell>;

  const lib = D();
  const months = [...new Set((st.all || []).map((s) => s.periodMonth).filter(Boolean))].sort().reverse();
  // Default to the most recent month that HAS statements (not the calendar month).
  const month = (pickedMonth && months.includes(pickedMonth)) ? pickedMonth : (months[0] || thisMonth());
  const forMonth = (st.all || []).filter((s) => s.periodMonth === month);
  const { byPlatform, total } = lib.aggregate(forMonth);

  return (
    <KShell tab="delivery">
      <KHeader big back={false} title={t('header.delivery', 'Delivery')}
        subtitle={months.length ? `${forMonth.length} of ${lib.MONTHLY_UPLOAD_LIMIT} statements · ${monthLabel(month)}` : 'DoorDash, Uber Eats & Grubhub'}
        right={
          <button onClick={() => nav.navigate('/delivery/upload')} aria-label="Upload statement"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--on)', padding: 0 }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
          </button>
        } />

      <div style={{ padding: '4px 20px 28px' }}>
        {/* Nothing booked this month → show which step of the pipeline lost it,
            rather than a silent $0 that could mean any of four different things. */}
        {st.why && <DeliveryWhyEmpty why={st.why} />}

        {/* Current month so far — restaurant share from deposits (statements not in yet). */}
        {st.mtd && (
          <div className="card" style={{ padding: 16, marginBottom: 18 }}>
            <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Restaurant share · {monthLabel(st.mtdMonth)} so far</div>
            <div style={{ marginTop: 2 }}>{$$(st.mtd.total, { tone: 'positive', style: { fontSize: 28, fontWeight: 650, letterSpacing: '-0.02em' } })}</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginBottom: 10 }}>deposited to date · this month's statements not uploaded yet</div>
            {['doordash', 'grubhub', 'ubereats'].map((p, i, arr) => {
              const meta = platformMeta(p);
              return (
                <div key={p} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--hairline)' : 'none' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: meta.color, display: 'inline-block' }} />
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{meta.label}</span>
                  </span>
                  {$$(st.mtd[p] || 0, { style: { fontSize: 14, fontWeight: 600, color: (st.mtd[p] || 0) > 0 ? 'var(--ink)' : 'var(--ink-3)' } })}
                </div>
              );
            })}
          </div>
        )}

        {/* Restaurant share totals from bank deposits — this year + last 12 months. */}
        {(st.ytd || st.ttm) && (
          <div className="card" style={{ padding: 16, marginBottom: 18 }}>
            <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 6 }}>Restaurant share · deposited from delivery</div>
            {[
              { k: 'ytd', label: `This year (${st.ytdYear})`, acc: st.ytd },
              { k: 'ttm', label: 'Last 12 months', acc: st.ttm },
            ].filter((x) => x.acc).map((x, i, arr) => (
              <div key={x.k} style={{ padding: '10px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--rule)' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ fontSize: 13.5, color: 'var(--ink-2)', fontWeight: 600 }}>{x.label}</span>
                  {$$(x.acc.total, { tone: 'positive', style: { fontSize: 22, fontWeight: 650, letterSpacing: '-0.02em' } })}
                </div>
                <div style={{ display: 'flex', gap: 14, marginTop: 6, flexWrap: 'wrap' }}>
                  {['doordash', 'grubhub', 'ubereats'].map((p) => {
                    const meta = platformMeta(p);
                    return (
                      <span key={p} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-3)' }}>
                        <span style={{ width: 7, height: 7, borderRadius: 2, background: meta.color, display: 'inline-block' }} />
                        {meta.label} {$$(x.acc[p] || 0, { style: { fontSize: 12, color: 'var(--ink-3)' } })}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
            <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 10 }}>Inferred from matched bank deposits — the dollars that actually landed.</div>
          </div>
        )}

        {(st.all || []).length === 0 ? (
          ((st.mtd || st.ytd || st.ttm) ? null : (
          <>
            <Empty icon={<IcoCar />} title="No statements yet"
              body="Upload a monthly statement PDF from DoorDash, Uber Eats or Grubhub to break down your delivery sales, fees and marketing." />
            <button className="btn lg" onClick={() => nav.navigate('/delivery/upload')} style={{ width: '100%', marginTop: 16, cursor: 'pointer' }}>
              Upload a statement
            </button>
          </>
          ))
        ) : (
          <>
            {/* Month picker */}
            {months.length > 1 && (
              <select value={month} onChange={(e) => setPickedMonth(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', marginBottom: 16, borderRadius: 10, border: '1px solid var(--rule)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 14 }}>
                {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
              </select>
            )}

            {forMonth.length === 0 ? (
              <div style={{ color: 'var(--on-dim)', fontSize: 14, padding: '20px 0' }}>No statements for {monthLabel(month)}.</div>
            ) : (
              <>
                {/* Headline: restaurant share (payout − tips) vs cost target */}
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 14 }}>
                  <div>
                    <div style={{ fontSize: 13, color: 'var(--on-dim)' }}>Restaurant share · {monthLabel(month)}</div>
                    <div>{$$(total.netPayoutCents - total.tipsCents, { tone: 'positive', style: { fontSize: 30, fontWeight: 650, letterSpacing: '-0.02em' } })}</div>
                    <div style={{ fontSize: 12, color: 'var(--on-dim)', marginTop: 2 }}>
                      {$$(total.tipsCents, { style: { fontSize: 12, color: 'var(--on-dim)' } })} tips to staff · {$$(total.netPayoutCents, { style: { fontSize: 12, color: 'var(--on-dim)' } })} deposited
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <CostPill b={total} big />
                    <div style={{ fontSize: 11, color: 'var(--on-dim)', marginTop: 2 }}>target &lt;<KPct value={COST_TARGET * 100} style={{ fontSize: 11 }}/></div>
                  </div>
                </div>
                <MonthlyTable byPlatform={byPlatform} total={total} />
              </>
            )}

            {/* Monthly trend across all uploaded months + this month so far (deposits) */}
            <TrendChart statements={st.all} mtd={st.mtd} mtdMonth={st.mtdMonth} />

            {/* Statements grouped into month cards (platforms alphabetical) */}
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--on-dim)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '24px 0 10px' }}>Statements</div>
            <DeliveryMonths statements={st.all || []} onOpen={(s) => nav.navigate(`/delivery/detail?id=${s.statementId}`)} />
          </>
        )}
      </div>
    </KShell>
  );
}

// Monthly reconciling table — one row per platform + a Total row.
// Columns reconcile left-to-right: Sales − Fees&comm − Mktg&promo − Adjustments
// − Tax = Payments (all signed; deductions show negative, adjustments may be +
// when tips/customer fees outweigh refunds). Horizontally scrollable on mobile.
const thBase = { fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap', padding: '8px 10px' };
const thL = { ...thBase, textAlign: 'left', position: 'sticky', left: 0, background: 'var(--surface)' };
const thR = { ...thBase, textAlign: 'right' };
const tdL = { textAlign: 'left', padding: '8px 10px', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', position: 'sticky', left: 0, background: 'var(--surface)' };

function MonthlyTable({ byPlatform, total }) {
  const lib = D();
  const anyTax = [...byPlatform, total].some((b) => (b.taxCents || 0) !== 0);
  const anyTips = [...byPlatform, total].some((b) => (b.tipsCents || 0) !== 0);
  const cols = [
    ['Sales', 'salesCents'],
    ['Fees & comm.', 'feesCommCents'],
    ['Mktg & promo', 'mktgPromoCents'],
    ['Adjustments', 'adjustmentsCents'],
    ...(anyTips ? [['Tips', 'tipsCents']] : []),
    ...(anyTax ? [['Tax', 'taxCents']] : []),
    ['Payments', 'paymentsCents'],
  ];
  const rows = byPlatform.map((p) => ({ key: p.platform || 'unknown', meta: platformMeta(p.platform), c: lib.reportColumns(p) }));
  const tc = lib.reportColumns(total);
  // Color rule: sales/payments (income) → positive green; fees, marketing,
  // adjustments, tax (expenses) → red when nonzero; tips pass through neutrally.
  const expenseKeys = new Set(['feesCommCents', 'mktgPromoCents', 'adjustmentsCents', 'taxCents']);
  const cell = (v, k) => {
    let tone;
    if (v === 0) tone = undefined;
    else if (k === 'salesCents' || k === 'paymentsCents') tone = 'positive';
    else if (expenseKeys.has(k)) tone = 'danger';
    return (
      <td key={k} style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
        {$$(v, { tone, style: { fontSize: 12.5, fontWeight: k === 'paymentsCents' ? 650 : 500 } })}
      </td>
    );
  };
  return (
    <>
      <div style={{ overflowX: 'auto', border: '1px solid var(--rule)', borderRadius: 14, background: 'var(--surface)' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 120 + cols.length * 78 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--rule)' }}>
              <th style={thL}>Platform</th>
              {cols.map(([h]) => <th key={h} style={thR}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} style={{ borderBottom: '1px solid var(--rule)' }}>
                <td style={tdL}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: r.meta.color, display: 'inline-block' }} />
                    {r.meta.label}
                  </span>
                </td>
                {cols.map(([, k]) => cell(r.c[k], k))}
              </tr>
            ))}
            <tr>
              <td style={{ ...tdL, fontWeight: 700 }}>Total</td>
              {cols.map(([, k]) => {
                let tone;
                if (tc[k] === 0) tone = undefined;
                else if (k === 'salesCents' || k === 'paymentsCents') tone = 'positive';
                else if (expenseKeys.has(k)) tone = 'danger';
                return (
                  <td key={k} style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {$$(tc[k], { tone, style: { fontSize: 12.5, fontWeight: 700 } })}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--on-dim)', marginTop: 8, lineHeight: 1.5 }}>
        Sales{anyTips ? ' + Tips' : ''} − Fees&nbsp;&amp;&nbsp;comm. − Mktg&nbsp;&amp;&nbsp;promo − Adjustments{anyTax ? ' − Tax' : ''} = Payments. Adjustments nets customer fees and refunds; Tips pass through to staff. Tax shown only where the platform withholds it (Grubhub).
      </div>
    </>
  );
}

function StatementRow({ s, onClick }) {
  const meta = platformMeta(s.platform);
  return (
    <button onClick={onClick} style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', borderBottom: '1px solid var(--rule)', padding: '12px 0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ width: 8, height: 8, borderRadius: 2, background: meta.color, display: 'inline-block' }} />
        <div>
          <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--ink)' }}>{meta.label}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{monthLabel(s.periodMonth)}</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {s.needsReview && <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--warning,#9a6700)', background: 'var(--warning-soft,#fff4e0)', padding: '2px 7px', borderRadius: 6 }}>Review</span>}
        {$$(s.grossSalesCents, { style: { fontSize: 14, fontWeight: 600, color: 'var(--ink-2)' } })}
        <Chev />
      </div>
    </button>
  );
}

// ── Statements grouped into month cards ──────────────────────
// Each month card: "Month YYYY" header with the effective rate justified right
// = (Σ payments − Σ tips) / (Σ gross sales − Σ tax). One line per platform,
// sorted alphabetically, tappable to the statement.
function DeliveryMonths({ statements, onOpen }) {
  const byMonth = new Map();
  for (const s of (statements || [])) {
    const k = s.periodMonth || 'unknown';
    if (!byMonth.has(k)) byMonth.set(k, []);
    byMonth.get(k).push(s);
  }
  const months = [...byMonth.keys()].sort().reverse();
  if (months.length === 0) return null;

  return months.map((mn) => {
    const list = byMonth.get(mn).slice().sort((a, b) => platformMeta(a.platform).label.localeCompare(platformMeta(b.platform).label));
    const sum = (f) => list.reduce((x, s) => x + (f(s) || 0), 0);
    const net = sum((s) => s.netPayoutCents);
    const tips = sum((s) => s.tipsCents);
    const gross = sum((s) => s.grossSalesCents);
    const taxAbs = sum((s) => Math.abs(s.taxCents || 0)); // tax is pass-through; remove from the sales base
    const denom = gross - taxAbs;
    const eff = denom > 0 ? (net - tips) / denom : null;
    // Effective rate is "share that reaches you" — below the 30% cost target
    // (i.e. < 70% kept) flags red.
    const effColor = eff == null ? 'var(--ink)' : eff < (1 - COST_TARGET) ? 'var(--danger)' : 'var(--ink)';
    return (
      <div key={mn} className="card" style={{ padding: '4px 16px', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '12px 0 8px', borderBottom: '1px solid var(--rule)' }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{monthLabel(mn)}</div>
          <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
            {eff != null
              ? <KPct value={eff * 100} style={{ fontSize: 15, fontWeight: 700, color: effColor }}/>
              : <span style={{ fontSize: 15, fontWeight: 700, color: effColor }}>—</span>}
            <span style={{ fontSize: 10.5, color: 'var(--ink-3)', marginLeft: 5 }}>effective</span>
          </div>
        </div>
        {list.map((s, i) => {
          const meta = platformMeta(s.platform);
          return (
            <button key={s.statementId} onClick={() => onOpen(s)}
              style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 0',
                borderBottom: i < list.length - 1 ? '1px solid var(--hairline)' : 'none' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: meta.color, display: 'inline-block' }} />
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{meta.label}</span>
                {s.needsReview && <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--warning,#9a6700)', background: 'var(--warning-soft,#fff4e0)', padding: '1px 6px', borderRadius: 6 }}>Review</span>}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {$$(s.netPayoutCents, { tone: 'positive', style: { fontSize: 14, fontWeight: 600 } })}
                <Chev />
              </span>
            </button>
          );
        })}
      </div>
    );
  });
}

// ── Monthly restaurant-share trend (Total / per-platform tabs) ──────────
// Bar LABELS are always actual dollars. The "Normalize to 30 days" toggle (off by
// default) only rescales the bar HEIGHTS to a 30-day rate so 28/29/30/31-day
// months compare fairly — the labels never move.
const TREND_PLATFORMS = ['doordash', 'ubereats', 'grubhub']; // stack order, bottom → top
/** Restaurant share of a statement: what's left after the platform's cut AND after
 *  the tips that pass straight through to staff. This is the operator's money. */
const shareOf = (s) => (s.netPayoutCents || 0) - (s.tipsCents || 0);
function TrendChart({ statements, mtd, mtdMonth }) {
  const [tab, setTab] = React.useState('all');
  const [norm, setNorm] = React.useState(false);
  const tabs = [['all', 'Total'], ['doordash', 'DoorDash'], ['ubereats', 'Uber Eats'], ['grubhub', 'Grubhub']];
  const months = [...new Set((statements || []).map((s) => s.periodMonth).filter(Boolean))].sort();
  const hasMtd = mtd && mtdMonth && !months.includes(mtdMonth);
  if (months.length === 0 && !hasMtd) return null;
  const stacked = tab === 'all'; // Total tab stacks per-platform segments (like the cash-flow chart)
  const accent = stacked ? BURGUNDY : platformMeta(tab).color;
  // Plot RESTAURANT SHARE (payout − tips) so the chart, the "Restaurant share ·
  // {month}" headline and the monthly table all report the same dollar.
  const data = months.map((m) => {
    const rows = statements.filter((s) => s.periodMonth === m && (tab === 'all' || s.platform === tab));
    const actual = rows.reduce((a, s) => a + shareOf(s), 0);
    const parts = Object.fromEntries(TREND_PLATFORMS.map((p) => [p,
      rows.reduce((a, s) => a + (s.platform === p ? shareOf(s) : 0), 0)]));
    const value = norm ? Math.round((actual / daysInMonthOf(m)) * 30) : actual;
    return { month: m, actual, value, parts };
  });
  if (hasMtd) {
    // In-progress month: plot the ACTUAL deposits so far — no projection — so the
    // bar agrees with the "Restaurant share · so far" tile above. Projecting from
    // elapsed days misleads badly early in the month (payouts land weekly).
    // Caveat: deposit-derived rows can't split tips out (sales.js writes
    // tipsCents: 0), so this bar is payout INCLUDING tips — a small overstatement
    // vs the statement months. Flagged in the tooltip and the footnote.
    const actual = tab === 'all' ? (mtd.total || 0) : (mtd[tab] || 0);
    const parts = Object.fromEntries(TREND_PLATFORMS.map((p) => [p, mtd[p] || 0]));
    data.push({ month: mtdMonth, actual, value: actual, parts, partial: true });
  }
  const vals = data.map((d) => d.value);
  const max = Math.max(1, ...vals);
  // Axis always starts at $0 (owner call 2026-08-04). A truncated baseline
  // exaggerates month-to-month variation and made each stacked segment's height
  // stop matching its dollar share; at $0 the bar and every platform segment are
  // both honestly proportional to the money.
  const base = 0;
  const denom = max;

  return (
    <div style={{ marginTop: 26 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--on-dim)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Monthly restaurant share</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--on-dim)', cursor: 'pointer' }}>
          <input type="checkbox" checked={norm} onChange={(e) => setNorm(e.target.checked)} />
          Normalize to 30 days
        </label>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {tabs.map(([id, label]) => {
          const on = tab === id;
          const c = id === 'all' ? BURGUNDY : platformMeta(id).color;
          return (
            <button key={id} onClick={() => setTab(id)}
              style={{ flex: 1, padding: '7px 4px', borderRadius: 8, fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
                border: on ? `1.5px solid ${c}` : '1px solid var(--rule)', background: on ? 'var(--surface)' : 'transparent', color: on ? 'var(--ink)' : 'var(--on-dim)' }}>
              {label}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        {/* Y-axis: max at top, $0 baseline at bottom */}
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 150, fontSize: 9.5, color: 'var(--on-dim)', textAlign: 'right', minWidth: 30 }}>
          <span>{kMoney(max)}</span>
          <span>{kMoney(base)}</span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 150, borderBottom: '1px solid var(--rule)' }}>
            {data.map((d) => {
              // Pixel heights (chart is 150px tall): mobile Safari mis-resolves
              // nested %-of-% flex heights, which can blank out stack segments.
              const barPx = Math.max(5, Math.round(((d.value - base) / denom) * 150));
              const title = d.partial
                ? `${monthLabel(d.month)} — ${money(d.actual)} deposited so far (includes tips; statements not uploaded yet)`
                : `${monthLabel(d.month)} — ${money(d.actual)} restaurant share (payout − tips)`;
              if (!stacked) {
                return (
                  <div key={d.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                    <div style={{ fontSize: 10.5, color: 'var(--on-soft)', marginBottom: 4, whiteSpace: 'nowrap' }}>{kMoney(d.actual)}</div>
                    <div title={title}
                      style={{ width: '100%', maxWidth: 52, height: barPx, background: accent, borderRadius: '4px 4px 0 0', opacity: d.partial ? 0.45 : 0.88 }} />
                  </div>
                );
              }
              // Stacked: one segment per platform, DoorDash at the bottom. With the
              // axis at $0 the whole bar is proportional to the month's payout, so
              // each segment's height is that platform's true dollar share of it.
              const present = TREND_PLATFORMS
                .map((p) => ({ p, cents: d.parts[p] || 0 }))
                .filter((s) => s.cents > 0);
              let used = 0;
              const segs = present.map((s, i) => {
                // Last (top) segment takes the remainder so rounding never leaves a gap.
                const px = i === present.length - 1 ? Math.max(0, barPx - used)
                  : Math.round((s.cents / Math.max(1, d.actual)) * barPx);
                used += px;
                return { p: s.p, px };
              }).filter((s) => s.px > 0);
              const topIdx = segs.length - 1;
              return (
                <div key={d.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                  <div style={{ fontSize: 10.5, color: 'var(--on-soft)', marginBottom: 4, whiteSpace: 'nowrap' }}>{kMoney(d.actual)}</div>
                  <div title={title} style={{ width: '100%', maxWidth: 52, display: 'flex', flexDirection: 'column-reverse', opacity: d.partial ? 0.45 : 0.88 }}>
                    {segs.length === 0
                      ? <div style={{ height: barPx, background: 'var(--surface-3)', borderRadius: '4px 4px 0 0' }} />
                      : segs.map((s, i) => (
                          <div key={s.p} style={{ height: s.px, flex: 'none', background: platformMeta(s.p).color, borderRadius: i === topIdx ? '4px 4px 0 0' : 0 }} />
                        ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 8, paddingTop: 6 }}>
            {data.map((d) => <div key={d.month} style={{ flex: 1, textAlign: 'center', fontSize: 10.5, color: 'var(--on-dim)' }}>{monthShort(d.month)}</div>)}
          </div>
        </div>
      </div>
      {stacked && (
        <div style={{ display: 'flex', gap: 14, marginTop: 8, flexWrap: 'wrap' }}>
          {TREND_PLATFORMS.map((p) => {
            const m = platformMeta(p);
            return (
              <span key={p} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: 'var(--ink-2)' }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: m.color, display: 'inline-block' }} />{m.label}
              </span>
            );
          })}
        </div>
      )}
      <div style={{ fontSize: 11, color: 'var(--on-dim)', marginTop: 8, lineHeight: 1.5 }}>
        Restaurant share per month — what's deposited after the platform's cut and after tips passed to staff. Labels are always actual dollars.
        {norm && ' Bar heights (not the labels) are normalized to a 30-day month so different-length months compare fairly.'}
        {hasMtd && ' The faded last bar is this month so far, from deposits — actual dollars, never projected, and tips are still included in it until the statement lands.'}
        {' Axis runs from $0, so bar heights — and each platform’s slice of a bar — are proportional to the dollars.'}
      </div>
    </div>
  );
}

// ── /delivery/upload ─────────────────────────────────────────
function DeliveryUpload({ dark = false }) {
  const nav = React.useContext(KanjoNav);
  const fileRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const [stage, setStage] = React.useState('pick'); // pick | processing | error
  const [error, setError] = React.useState(null);

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.type && file.type !== 'application/pdf') { setError('Please choose a PDF statement.'); return; }
    setError(null); setBusy(true); setStage('processing');
    try {
      // Platform + period are read off the statement itself — no need to ask.
      const saved = await D().uploadDeliveryStatement(file);
      nav.navigate(`/delivery/detail?id=${saved.statementId}`);
    } catch (err) {
      const msg = err?.code === 'DELIVERY_UPLOAD_LIMIT' ? err.message
        : err?.code === 'DELIVERY_PRO_REQUIRED' ? 'Delivery analysis requires Pro.'
        : err?.code === 'DELIVERY_NO_PAYOUT' ? err.message
        : `Couldn't process that statement: ${err?.message || err}`;
      setError(msg); setStage('error'); setBusy(false);
    }
  };

  return (
    <KShell tab="delivery" noNav>
      <KHeader title="Upload statement" onBack={() => nav.navigate('/delivery')} />
      <div style={{ padding: '8px 20px 28px' }}>
        {stage === 'processing' ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--on-soft)' }}>
            <div className="spinner" style={{ margin: '0 auto 18px' }} />
            <div style={{ fontSize: 15, fontWeight: 600 }}>Reading your statement…</div>
            <div style={{ fontSize: 13, color: 'var(--on-dim)', marginTop: 6 }}>Detecting the platform and month, and extracting sales, commissions, fees and marketing. This takes a few seconds.</div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 14, color: 'var(--on-soft)', lineHeight: 1.5, marginBottom: 24 }}>
              Download the <strong>monthly statement PDF</strong> from DoorDash, Uber Eats or Grubhub and upload it here. We read the platform, month and all the numbers off the statement automatically — you can correct anything after.
            </div>

            {error && <ErrorBanner style={{ marginBottom: 14 }}>{error}</ErrorBanner>}

            <input ref={fileRef} type="file" accept="application/pdf" onChange={onFile} style={{ display: 'none' }} />
            <button className="btn lg" disabled={busy} onClick={() => fileRef.current?.click()}
              style={{ width: '100%', cursor: busy ? 'wait' : 'pointer' }}>
              Choose PDF
            </button>
          </>
        )}
      </div>
    </KShell>
  );
}
const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 };

// ── /delivery/detail — review / correct one statement ────────
function DeliveryDetail({ dark = false }) {
  const nav = React.useContext(KanjoNav);
  const id = useHashQuery('id');
  const [s, setS] = React.useState(undefined); // undefined=loading, null=missing
  const [edit, setEdit] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!id) { setS(null); return; }
    const all = await D().listStatements();
    setS(all.find((x) => x.statementId === id) || null);
  }, [id]);
  React.useEffect(() => { load(); }, [load]);

  if (s === undefined) return <KShell tab="delivery" noNav><KHeader title="Statement" onBack={() => nav.navigate('/delivery')} /><div style={{ padding: 20, color: 'var(--on-dim)' }}>Loading…</div></KShell>;
  if (s === null) return <KShell tab="delivery" noNav><KHeader title="Statement" onBack={() => nav.navigate('/delivery')} /><Empty title="Not found" body="This statement no longer exists." /></KShell>;

  const meta = platformMeta(s.platform);
  const fields = [
    ['grossSalesCents', 'Gross sales'],
    ['commissionCents', 'Commission'],
    ['marketingCents', 'Marketing'],
    ['promotionsCents', 'Promotions'],
    ['feesCents', 'Fees'],
    ['otherCents', 'Adjustments'],
    ['taxCents', 'Tax (platform-remitted)'],
    ['tipsCents', 'Tips (staff)'],
    ['netPayoutCents', 'Net payout'],
  ];

  const startEdit = () => setEdit(Object.fromEntries(fields.map(([k]) => [k, ((s[k] || 0) / 100).toFixed(2)])));
  const saveEdit = async () => {
    setBusy(true);
    try {
      const patch = {};
      for (const [k] of fields) patch[k] = Math.round((Number(edit[k]) || 0) * 100);
      await D().updateStatement(id, patch);
      setEdit(null); await load();
    } catch (e) { console.warn('[delivery] save failed', e); }
    finally { setBusy(false); }
  };
  const onDelete = async () => {
    setBusy(true);
    try { await D().deleteStatement(id); nav.navigate('/delivery'); }
    catch (e) { console.warn('[delivery] delete failed', e); setBusy(false); }
  };

  return (
    <KShell tab="delivery" noNav>
      <KHeader title={meta.label} onBack={() => nav.navigate('/delivery')} />
      <div style={{ padding: '4px 20px 28px' }}>
        <div style={{ marginBottom: 16 }}>
          {s.storeName && <div style={{ fontSize: 15, fontWeight: 600 }}>{s.storeName}</div>}
          <div style={{ fontSize: 13, color: 'var(--on-dim)' }}>
            {monthLabel(s.periodMonth)}{s.statementNumber ? ` · ${s.statementNumber}` : ''}
          </div>
        </div>

        {s.needsReview && !edit && (
          <div style={{ background: 'var(--warning-soft,#fff4e0)', color: 'var(--warning,#9a6700)', fontSize: 13, lineHeight: 1.5, padding: '10px 12px', borderRadius: 10, marginBottom: 16 }}>
            We weren't fully confident reading this one — the numbers don't quite reconcile to the payout. Tap <strong>Edit</strong> to check and correct them.
          </div>
        )}

        {edit ? (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--rule)', borderRadius: 14, padding: 16 }}>
            {fields.map(([k, label]) => (
              <div key={k} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--rule)' }}>
                <span style={{ fontSize: 14, color: 'var(--ink-2)' }}>{label}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ color: 'var(--ink-3)' }}>$</span>
                  <input type="number" inputMode="decimal" step="0.01" value={edit[k]}
                    onChange={(e) => setEdit({ ...edit, [k]: e.target.value })}
                    style={{ width: 110, textAlign: 'right', padding: '6px 8px', borderRadius: 8, border: '1px solid var(--rule)', background: 'var(--bg)', color: 'var(--on)', fontSize: 14 }} />
                </span>
              </div>
            ))}
          </div>
        ) : (() => {
          // Read view: summary card (gross → net + rate) then grouped details.
          const c = D().reportColumns(s);
          const rate = s.grossSalesCents > 0 ? s.netPayoutCents / s.grossSalesCents : null;
          const detailRows = [
            ['Fees & commissions', c.feesCommCents],
            ['Marketing', c.mktgPromoCents],
            ['Adjustments', c.adjustmentsCents],
            ['Tips (staff)', c.tipsCents],
            ...(c.taxCents !== 0 ? [['Tax (platform-remitted)', c.taxCents]] : []),
          ];
          return (
            <>
              <div className="card" style={{ padding: 18, marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>Gross sales (excl. tax)</div>
                    <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em' }}>{money(s.grossSalesCents)}</div>
                  </div>
                  <span aria-hidden="true" style={{ fontSize: 20, color: 'var(--ink-3)', padding: '0 4px' }}>→</span>
                  <div style={{ textAlign: 'right', minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>Net payout</div>
                    <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em' }}>{money(s.netPayoutCents)}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>{rate != null ? pct(rate) : '—'} of gross sales</div>
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--on-dim)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 8px', paddingLeft: 4 }}>Details</div>
              <div className="card" style={{ padding: '4px 16px' }}>
                {detailRows.map(([label, v], i) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: i < detailRows.length - 1 ? '1px solid var(--rule)' : 'none' }}>
                    <span style={{ fontSize: 14, color: 'var(--ink-2)' }}>{label}</span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: v < 0 ? 'var(--ink)' : 'var(--ink)' }}>{money(v)}</span>
                  </div>
                ))}
              </div>
            </>
          );
        })()}

        {/* Itemized lines captured from the statement (read-only transparency). */}
        {!edit && Array.isArray(s.lines) && s.lines.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--on-dim)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>Statement detail</div>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--rule)', borderRadius: 14, padding: '4px 16px' }}>
              {s.lines.map((ln, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < s.lines.length - 1 ? '1px solid var(--rule)' : 'none' }}>
                  <span style={{ fontSize: 13, color: 'var(--ink-2)', marginRight: 12 }}>{ln.label || '—'}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', color: ln.amountCents < 0 ? 'var(--danger)' : 'var(--ink)' }}>{money(ln.amountCents)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          {edit ? (
            <>
              <button className="btn ghost lg" disabled={busy} onClick={() => setEdit(null)} style={{ flex: 1, cursor: 'pointer' }}>Cancel</button>
              <button className="btn lg" disabled={busy} onClick={saveEdit} style={{ flex: 1, cursor: 'pointer' }}>Save</button>
            </>
          ) : (
            <>
              <button className="btn ghost lg" disabled={busy} onClick={onDelete} style={{ flex: 1, cursor: 'pointer', color: 'var(--danger)' }}>Delete</button>
              <button className="btn lg" onClick={startEdit} style={{ flex: 1, cursor: 'pointer' }}>Edit</button>
            </>
          )}
        </div>
      </div>
    </KShell>
  );
}


// Car icon — used for the nav tab + empty states.
function IcoCar() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 11l1.5-4.5A2 2 0 018.4 5h7.2a2 2 0 011.9 1.5L19 11"/>
      <path d="M3 11h18v5a1 1 0 01-1 1h-1a1 1 0 01-1-1v-1H6v1a1 1 0 01-1 1H4a1 1 0 01-1-1z"/>
      <circle cx="7.5" cy="14" r="0.6" fill="currentColor"/>
      <circle cx="16.5" cy="14" r="0.6" fill="currentColor"/>
    </svg>
  );
}

Object.assign(window, { DeliveryMenu, DeliveryUpload, DeliveryDetail });
